import { AbstractControl, AsyncValidatorFn, FormGroup, ValidationErrors } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SgaApiService } from '../services/sga-api.service';
import { BenchmarkInputsInterface } from '../interfaces/benchmarkInputs.interface';
import { CalcBenchmarkResInterface } from '../interfaces/calc-benchmark-res.interface';
import { SgaErrorInterface, SgaValidationErrorResInterface } from '../interfaces/api-requests.interface';
import { ElementRef } from '@angular/core';
import sgaFormStructure from '../utils/sga-form-structure';
import { FocusOnFirstErrorField } from '../utils/focusOnFirstErrorField';
import { SpecificationSheetHeaderInputsInterface } from '../interfaces/specsheet-header-inputs.interface';
import { isNull } from "util";

const getNameFomControl = (control: AbstractControl): string => control.parent && control.parent.controls &&
  Object.keys(control.parent.controls).find(name => control.parent.controls[name] === control);

const getControlNameFromReqError = (error: SgaErrorInterface): {name: string, error: string} => {
  if (!error) { return null; }

  const propertyName = error.propertyName && error.propertyName.slice(16, error.propertyName.length);

  return {
    name: propertyName ? propertyName.charAt(0).toLocaleLowerCase() + propertyName.slice(1) : 'UNKNOWN',
    error: error.errorMessage || 'UNKNOWN_ERROR'
  };
};

export const validateBenchmarkInput = (service: SgaApiService, isNull?: boolean): AsyncValidatorFn =>
  (control: AbstractControl): Observable<ValidationErrors> => {
    const fg: FormGroup = control && control.root as FormGroup;
    const name = getNameFomControl(control);

    if (!fg || !name || !service || !control || (control.pristine && !control.touched) || (isNull && !control.value)) {
      return of(null);
    }

    //const { selectedUnits, benchmarkInputs } = fg.getRawValue();
    //below are HARD CODED values added by RPU on 25/05/2022 to overcome problem with missing units required for sucessful Validation.
    //this MUST be removed at some point and the line above (line 36 ) MUST be re-instated
    var { selectedUnits, benchmarkInputs, specSheetHeader } = fg.getRawValue();
    if (selectedUnits) {
      selectedUnits.smallVolumetricFlowUnitSelected = 76,
      selectedUnits.smallMassFlowUnitSelected = 84
    }
    ////////////////////////////////////////////////////////////////////////////////////////

    //validate if o2ScavengingChemicalsCostSavings is less than totalChemicalCostPerYear
    if (name == 'o2ScavengingChemicalsCostSavings' || name == 'totalChemicalCostPerYear') {
      const totalChemicalCostPerYear = fg.get('benchmarkInputs.totalChemicalCostPerYear').value;
      const o2ScavengingChemicalsCostSavings = fg.get('benchmarkInputs.o2ScavengingChemicalsCostSavings').value;

      if (totalChemicalCostPerYear != null && o2ScavengingChemicalsCostSavings != null && (o2ScavengingChemicalsCostSavings > totalChemicalCostPerYear)) {
        let message = totalChemicalCostPerYear.toString();
        let error = 'O2_SCAVENGING_CHEMICALS_COST_CANNOT_BE_REATER_THAN_TOTAL_CHEMICAL_COSTS_PER_YEAR';
        return of({ error, message });
      }
      else if (totalChemicalCostPerYear != null && o2ScavengingChemicalsCostSavings != null && (o2ScavengingChemicalsCostSavings < totalChemicalCostPerYear)) {
        fg.get('benchmarkInputs.totalChemicalCostPerYear').setErrors(null);
        fg.get('benchmarkInputs.o2ScavengingChemicalsCostSavings').setErrors(null);
      }
    }

    return service.benchmarkValidate(name as keyof BenchmarkInputsInterface, {
      selectedUnits: selectedUnits,
      benchmarkInputs: { ...benchmarkInputs, [name]: control.value },
      specSheetHeader: specSheetHeader,
    }).pipe(
      map(res => {
        if (res && !res.isValid) {
          const error = res.errors && res.errors[0] && res.errors[0].errorMessage;
          const customState = res.errors && res.errors[0] && res.errors[0].customState;
          let message = res.error && res.error.title || '';

          if (customState || customState === 0) {
            message = `${Math.round(customState * 100) / 100}`;
          }

          return { error, message };
        }

        return null;
      }),
      catchError((err: HttpErrorResponse) => err && of({message: err.error && err.error.title || ''})),
    );
  };

export const benchmarkCalculationValidator = (res, form: FormGroup, {nativeElement}: ElementRef): CalcBenchmarkResInterface => {
  const err = res as SgaValidationErrorResInterface;

  if (err && err.errors && err.errors.length) {
    let firstErrorFieldName: string;

    for (const error of err.errors) {
      const controlError = getControlNameFromReqError(error);
      const control = form.get(`benchmarkInputs.${controlError.name}`);

      if (control && control.status === 'VALID') {
        if (!firstErrorFieldName) {
          firstErrorFieldName = controlError.name;
        }
        control.setErrors({error: controlError.error, message: error && error.customState});
        control.markAsTouched({onlySelf: true});
      }
    }

    if (firstErrorFieldName) {
      const element: HTMLInputElement = nativeElement.querySelector(`[ng-reflect-name="${firstErrorFieldName}"]`);
      FocusOnFirstErrorField(sgaFormStructure, firstErrorFieldName, element);
    }

    return null;
  }

  return res as CalcBenchmarkResInterface;
};
